/** External **/
import _ from "lodash";
import React from "react";

/** Design System **/
import {
  AssessmentApplicableOutlined,
  PlaceholderdotsOutlined,
  ClipboardArrowOutlined,
} from "@toddle-edu/ds-icons";

/** Modules **/
import { getSettingValue } from "modules/PermissionModule";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

/** Constants */
import {
  BEHAVIOUR_ANALYTICS,
  TABLE_DEFAULT_COLUMNS,
  TABLE_CONFIG_PERMS,
  ANALYTICS_ENTITY_TYPE_ENUMS,
  DEFAULT_TABLE_COLUMN_WIDTH,
  DYNAMIC_COLUMNS_INSERT_INDEX_VALUE,
  SEVERITIES_COLOR_MAP,
  INCIDENT_STATUS,
  INCIDENT_DETAILED_BY_CATEGORY,
  INCIDENT_DETAILED_BY_GRADE,
  INCIDENT_DETAILED_BY_STUDENT,
  BEHAVIOUR_NOMENCLATURE_CONSTANTS,
  BEHAVIOUR_MODULES,
} from "ToddleAnalytics/modules/Constants";
import {
  BEHAVIOUR_LEVEL_CHART_SORT_ORDER,
  ENTITY_TYPE_TO_METADATA_ID_MAP,
  BEHAVIOUR_SENTIMENT_COLOR_MAP,
  ACTION_OVERVIEW_CHART_COLORS,
  BEHAVIOUR_SENTIMENT_CHART_SORT_ORDER,
  STAFF_ROLES,
} from "ToddleAnalytics/routes/Behaviour/modules/BehaviourAnalyticsConstants";

/** Utils */
import {
  getAnalyticsEntityQueryVariables,
  getDateRange,
  getAnalyticsEntityColors,
  trimNumber,
  getEntityIdNameMap,
  getMapFromArrayOfColumnIds,
  extractIdsFromUrlSearchParams,
  getNomenclatureLocaleParams,
} from "ToddleAnalytics/modules/Utils";

/** Graphql Helpers */
import { getAnalyticsEntityFromCache } from "ToddleAnalytics/modules/ToddleAnalyticsGraphqlHelpers";

export const getDefaultColumns = ({ subModule, tableName }) => {
  return TABLE_DEFAULT_COLUMNS[BEHAVIOUR_ANALYTICS][subModule][tableName];
};

const getOldConfigValue = ({ subModule, curriculumProgramId }) => {
  return getSettingValue({
    name: TABLE_CONFIG_PERMS[BEHAVIOUR_ANALYTICS][subModule],
    currentCurriculumProgramId: curriculumProgramId,
  });
};

export const getBehaviourAnalyticsConfiguredColumns = ({
  subModule,
  tableName,
  curriculumProgramId,
}) => {
  const behaviourTableConfiguration = getSettingValue({
    name: TABLE_CONFIG_PERMS[BEHAVIOUR_ANALYTICS][subModule],
    currentCurriculumProgramId: curriculumProgramId,
  });

  const configuredColumnsFromSettings = _.get(
    behaviourTableConfiguration,
    tableName
  );
  const defaultColumns = getDefaultColumns({
    subModule,
    tableName,
    curriculumProgramId,
  });

  return getMapFromArrayOfColumnIds(
    _.isEmpty(configuredColumnsFromSettings)
      ? defaultColumns
      : configuredColumnsFromSettings
  );
};

export const getUpdatedBehaviourAnalyticsSettingsValue = ({
  configuredColumns,
  subModule,
  tableName,
  curriculumProgramId,
}) => {
  const oldConfigValue = getOldConfigValue({ subModule, curriculumProgramId });
  const newConfigValue = {
    ...oldConfigValue,
    [tableName]: configuredColumns,
  };

  const configurationName = TABLE_CONFIG_PERMS[BEHAVIOUR_ANALYTICS][subModule];

  return [
    {
      name: configurationName,
      value: JSON.stringify(newConfigValue),
    },
  ];
};

const getShouldShowFullScreenLoader = ({
  showFullSkeletonLoader = null,
  isLoading,
  isGraphDataInCache,
  isTableDataInCache,
  areTableFiltersDefault,
}) => {
  if (!_.isNull(showFullSkeletonLoader)) return showFullSkeletonLoader;

  return (
    isLoading &&
    !isGraphDataInCache &&
    !isTableDataInCache &&
    areTableFiltersDefault
  ); //Initial loading when data is not present in cache
};

export const getBehaviourCardLoadingStates = ({
  isLoading,
  tableData,
  graphData,
  showFullSkeletonLoader = null,
  isGraphDataInCache,
  isTableDataInCache,
  networkStatus,
  areTableFiltersDefault,
}) => {
  const _showFullSkeletonLoader = getShouldShowFullScreenLoader({
    isLoading,
    isTableDataInCache,
    isGraphDataInCache,
    showFullSkeletonLoader,
    areTableFiltersDefault,
  });
  const showInnerSkeletonLoader =
    !_showFullSkeletonLoader && !isTableDataInCache;
  const showPaginationLoader = _.isEqual(networkStatus, 3);
  const showTableErrorState = !isLoading && !isTableDataInCache;
  const showGraphErrorState = !isLoading && !isGraphDataInCache;
  const showCombinedErrorState = showTableErrorState && showGraphErrorState;
  const showCombinedNoDataState =
    _.isEmpty(tableData) &&
    _.isEmpty(graphData) &&
    !_.isUndefined(tableData) &&
    !_.isUndefined(graphData) &&
    areTableFiltersDefault;

  return {
    showTableErrorState,
    showPaginationLoader,
    showFullSkeletonLoader: _showFullSkeletonLoader,
    showInnerSkeletonLoader,
    showCombinedErrorState,
    showCombinedNoDataState,
  };
};

export const getBehaviourTableQueryVariables = ({
  orgId,
  curriculumProgramId,
  entityType,
  viewType,
  gradeId,
  studentId,
  behaviourCategoryId,
  paginationFilters,
  searchText,
  rangeStart,
  rangeEnd,
}) => {
  return {
    id: orgId,
    tableMetricFilters: {
      moduleType: BEHAVIOUR_ANALYTICS,
      behaviourIndividualMetricsFilter: {
        curriculumProgramIds: [curriculumProgramId],
        entityType,
        viewType,
        gradeIds: gradeId ? [gradeId] : undefined,
        studentIds: studentId ? [studentId] : undefined,
        behaviourCategoryIds: behaviourCategoryId
          ? [behaviourCategoryId]
          : undefined,
        rangeStart,
        rangeEnd,
      },
      paginationFilters,
      searchText,
    },
  };
};

export const getBehaviourChartQueryVariables = ({
  orgId,
  curriculumProgramIds,
  xAxisKey,
  yAxisKey,
  gradeIds,
  rangeStart,
  rangeEnd,
  behaviourCategoryIds,
  studentIds,
  timeSeriesInterval,
}) => {
  return {
    id: orgId,
    graphDataFilters: {
      moduleType: BEHAVIOUR_ANALYTICS,
      xAxisKey,
      yAxisKey,
      behaviourGraphFilter: {
        curriculumProgramIds,
        rangeStart,
        rangeEnd,
        gradeIds,
        studentIds,
        timeSeriesInterval,
        behaviourCategoryIds,
      },
    },
  };
};

export const getBehaviourCategoryEntityQueryVariables = ({
  withCurriculumFilter = false,
} = {}) => {
  const { org_id: orgId } = getUserInfo();
  const { behaviourCategory } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const queryVariables = {
    id: orgId,
    analyticsEntityFilterInput: {
      entityType: behaviourCategory,
    },
  };

  if (withCurriculumFilter) {
    const { selectedCurriculumId } = extractIdsFromUrlSearchParams();
    queryVariables["analyticsEntityFilterInput"][
      "curriculumProgramIds"
    ] = _.compact([selectedCurriculumId]);
  }

  return queryVariables;
};

export const getBehaviourCategoryEntityOptions = ({
  withCurriculumFilter = false,
} = {}) => {
  const queryVariables = getBehaviourCategoryEntityQueryVariables({
    withCurriculumFilter,
  });
  const queryData = getAnalyticsEntityFromCache(queryVariables);
  const analyticsEntityData = _.get(queryData, "analytics.analyticsEntityData");
  const categories = _.get(analyticsEntityData, "edges", []);

  return _.map(categories, ({ entityId, name }) => {
    return { key: entityId, value: entityId, label: name };
  });
};

export const getBehaviourSeverityEntityQueryVariables = () => {
  const { org_id: orgId } = getUserInfo();
  const { behaviourSeverity } = ANALYTICS_ENTITY_TYPE_ENUMS;

  return {
    id: orgId,
    analyticsEntityFilterInput: {
      entityType: behaviourSeverity,
    },
  };
};

const _getBehaviourCategoryColors = () => {
  const { behaviourCategory } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const queryVariables = getAnalyticsEntityQueryVariables({
    entityType: behaviourCategory,
  });
  const queryData = getAnalyticsEntityFromCache(queryVariables);

  const categoriesData = _.get(
    queryData,
    "analytics.analyticsEntityData.edges",
    []
  );

  return _.reduce(
    categoriesData,
    (result, data) => {
      const categoryId = _.get(data, "entityId");
      const color = _.get(data, "metaData.color");
      result[categoryId] = color;
      return result;
    },
    {}
  );
};

export const getBehaviourCategoryColors = _.memoize(
  _getBehaviourCategoryColors
);

export const getDateRangeFromUrlParams = urlSearchParams => {
  const extractedUrlParams = extractIdsFromUrlSearchParams(urlSearchParams);

  return {
    ...getDateRange(extractedUrlParams),
    dateViewType: _.get(extractedUrlParams, "dateViewType"),
  };
};

const _getBehaviourSeverityColors = () => {
  const queryVariables = getBehaviourSeverityEntityQueryVariables();
  const queryData = getAnalyticsEntityFromCache(queryVariables);

  const severitiesData = _.get(
    queryData,
    "analytics.analyticsEntityData.edges",
    []
  );

  return _.reduce(
    severitiesData,
    (result, data) => {
      const severityId = _.get(data, "entityId");
      const color = _.get(data, "metaData.color");
      result[severityId] = _.get(SEVERITIES_COLOR_MAP, color, "neutral");
      return result;
    },
    {}
  );
};

export const getBehaviourSeverityColors = _.memoize(
  _getBehaviourSeverityColors
);

export const getCategoryColumnData = ({ id, label, fieldUID }) => {
  return {
    id,
    label,
    isDynamicColumn: true,
    configurationId: "behaviourCategory",
    configurationLabel: "common:category_plural",
    group: "toddleAnalytics:metrics",
    type: "TEXTCOLUMN",
    alignment: "right",
    fieldUID,
    sortType: "toggle",
    style: { minWidth: DEFAULT_TABLE_COLUMN_WIDTH, fontSize: "14px" },
  };
};

const _insertCategoryColumnsBasedOnCurriculumProgramId = ({
  tableColumns,
  tableName,
  sentiments = [],
}) => {
  const queryVariables = getBehaviourCategoryEntityQueryVariables();
  const queryData = getAnalyticsEntityFromCache(queryVariables);

  const {
    selectedCurriculumId: curriculumProgramId,
  } = extractIdsFromUrlSearchParams();

  const behaviourCategories = _.get(
    queryData,
    "analytics.analyticsEntityData.edges",
    []
  );

  const filteredBehaviourCategories = _.filter(
    behaviourCategories,
    category => {
      const behaviourCurriculumProgramIds = _.get(
        category,
        "curriculumProgramIds"
      );
      let shouldIncludeCategory = _.includes(
        behaviourCurriculumProgramIds,
        curriculumProgramId
      );

      if (_.size(sentiments) > 0) {
        const sentimentType = _.get(category, "metaData.sentiment_name");
        shouldIncludeCategory =
          _.includes(sentiments, sentimentType) && shouldIncludeCategory;
      }
      return shouldIncludeCategory;
    }
  );

  const dynamicColumns = _.map(filteredBehaviourCategories, category => {
    const entityId = _.get(category, "entityId");
    const label = _.get(category, "name");
    const categoryId = `BEHAVIOUR_CATEGORY-${entityId}`;
    const columnData = getCategoryColumnData({
      id: categoryId,
      label,
      fieldUID: categoryId,
    });
    return columnData;
  });

  const insertPosition = DYNAMIC_COLUMNS_INSERT_INDEX_VALUE[tableName];

  const beforeColumns = _.slice(tableColumns, 0, insertPosition);
  const afterColumns = _.slice(
    tableColumns,
    insertPosition,
    _.size(tableColumns)
  );

  return [...beforeColumns, ...dynamicColumns, ...afterColumns];
};

export const insertCategoryColumnsBasedOnCurriculumProgramId = _.memoize(
  _insertCategoryColumnsBasedOnCurriculumProgramId,
  ({ tableColumns, tableName, sentiments = [] }) =>
    JSON.stringify({
      tableColumns,
      tableName,
      sentiments,
    })
);

export const getFormattedTableData = ({
  FIELDUID_COLUMNID_MAPPING,
  tableData,
  tableName,
}) => {
  const formatTableRow = tableRow =>
    _.reduce(
      tableRow,
      (resultantRow, { field, value, metaData }) => {
        const val = _.size(value) > 1 ? value : _.get(value, "0", "");
        if (!_.isEmpty(metaData)) {
          /**
           * Behaviour analytics metadata is complex from normal metaData, so we use different function
           * for this module
           */

          const columnId = _.get(FIELDUID_COLUMNID_MAPPING, field);
          switch (field) {
            case "BEHAVIOUR_CATEGORY":
              {
                const behaviourCategoryId = _.get(
                  metaData,
                  "behaviour_category_id"
                );
                /**
                 *Some tables have differnt purpose of id which are being used for dynamic columns
                 */
                if (
                  _.includes(
                    [
                      INCIDENT_DETAILED_BY_CATEGORY,
                      INCIDENT_DETAILED_BY_GRADE,
                      INCIDENT_DETAILED_BY_STUDENT,
                    ],
                    tableName
                  )
                ) {
                  resultantRow[columnId] = {
                    category: val,
                    behaviourCategoryId,
                  };
                } else {
                  resultantRow[
                    `BEHAVIOUR_CATEGORY-${behaviourCategoryId}`
                  ] = val;
                }
              }
              break;
            case "INCIDENT_SEVERITY":
              {
                const behaviourSeverityId = _.get(
                  metaData,
                  "behaviour_severity_id"
                );
                resultantRow[columnId] = {
                  severity: val,
                  behaviourSeverityId,
                };
              }
              break;
            case "INCIDENT_STUDENT_INVOLVED":
              {
                const id = _.get(metaData, "student_id");
                const name = _.get(metaData, "name");
                const grade = _.get(metaData, "grade");
                const profileUrl = _.get(metaData, "profile_url");
                resultantRow[columnId] = {
                  id,
                  name,
                  grade,
                  profileUrl,
                };
              }
              break;
            case "INCIDENT_CREATED_BY":
              {
                const id = _.get(metaData, "staff_id");
                const name = _.get(metaData, "name");
                const profileUrl = _.get(metaData, "profile_url");
                resultantRow[columnId] = {
                  id,
                  name,
                  profileUrl,
                };
              }
              break;
            case "INCIDENT_ACTION":
              {
                const actionsApplied = _.get(metaData, "action_applied");
                const actionsAdded = _.get(metaData, "action_added");
                resultantRow[columnId] = {
                  actionsApplied,
                  actionsAdded,
                };
              }
              break;
            case "SHARED_WITH":
              {
                resultantRow[columnId] = metaData;
              }
              break;
            default:
              null;
          }
        } else {
          const columnId = FIELDUID_COLUMNID_MAPPING[field];
          resultantRow[columnId] = val;
        }
        return resultantRow;
      },
      {}
    );

  return _.map(tableData, tableRow => formatTableRow(tableRow));
};

export const getEntityValueMap = ({
  yAxisDataPoints,
  allEntityIds,
  entityType,
  setZeroForNull = true,
}) => {
  const entityIdNameMap = getEntityIdNameMap({ entityType });

  const entitiesNameValueMap = _.reduce(
    _.keys(entityIdNameMap),
    (result, id) => {
      if (_.includes(allEntityIds, id)) {
        const name = entityIdNameMap[id];
        result[name] = setZeroForNull ? 0 : null;
      }
      return result;
    },
    {}
  );

  return _.reduce(
    yAxisDataPoints,
    (result, entity) => {
      const entityId = _.get(
        entity,
        `metaData.${ENTITY_TYPE_TO_METADATA_ID_MAP[entityType]}`
      );
      if (_.isEmpty(entityId)) return result;

      const value = trimNumber(_.get(entity, "value", 0));
      const behaviourCategoryName = entityIdNameMap[entityId];

      if (_.isEmpty(behaviourCategoryName)) return result;

      result[behaviourCategoryName] = value;
      return result;
    },
    entitiesNameValueMap
  );
};

const getFormattedDataForSimpleBehaviourBarChart = ({
  type,
  module,
  graphData,
}) => {
  const {
    grade,
    behaviourSeverity,
    behaviourSentiment,
  } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const metadataId = ENTITY_TYPE_TO_METADATA_ID_MAP[type];
  const removeEmptyBarGroups = shouldRemoveEmptyBarGroups({ type, module });

  let areAllValuesZero = true;
  const formattedGraphData = _.reduce(
    graphData,
    (result, data) => {
      const xAxis = _.startCase(_.toLower(_.get(data, "xAxis")));
      const yAxis = _.toNumber(_.get(data, "yAxis.0.value"), 0);
      const id = _.get(data, `yAxis.0.metaData.${metadataId}`);

      /** If value is zero, we hide that bar for a given chart type */
      if (yAxis === 0 && removeEmptyBarGroups) {
        return result;
      }

      /** Checks if all bar data is zero */
      if (yAxis !== 0) areAllValuesZero = false;

      result.push({
        id,
        xAxis,
        yAxis,
      });

      return result;
    },
    []
  );

  /** To show No data found state */
  if (areAllValuesZero) return [];

  /** Sort and return the formatted data based on the type */
  if (_.includes([grade], type)) return formattedGraphData;

  if (type === behaviourSentiment)
    return _.orderBy(
      formattedGraphData,
      data => BEHAVIOUR_SENTIMENT_CHART_SORT_ORDER[data.xAxis],
      "asc"
    );

  if (type === behaviourSeverity)
    return _.orderBy(
      formattedGraphData,
      data => BEHAVIOUR_LEVEL_CHART_SORT_ORDER[data.xAxis],
      "asc"
    );

  return _.orderBy(formattedGraphData, data => _.toLower(data.xAxis), "asc");
};

const getFormattedDataForStackedBehaviourBarChart = ({
  type,
  module,
  graphData,
}) => {
  const {
    grade,
    behaviourLocation,
    behaviourCategory,
  } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const entityType = _.includes([grade, behaviourLocation], type)
    ? behaviourCategory
    : type;

  const entityIds = getUniqueEntityIds({
    entityType,
    graphData,
  });

  /** To show empty state when all values are zero */
  if (_.isEmpty(entityIds)) return [];

  const removeEmptyBarGroups = shouldRemoveEmptyBarGroups({ type, module });

  const formattedGraphData = _.reduce(
    graphData,
    (result, data) => {
      const xAxis = _.get(data, "xAxis", "");

      const yAxis = _.get(data, "yAxis", []);
      const incidentCategoryValueMap = getEntityValueMap({
        yAxisDataPoints: yAxis,
        allEntityIds: entityIds,
        entityType,
      });

      const areAllValuesZero = _.every(
        incidentCategoryValueMap,
        value => value === 0
      );

      /** If all y-axis values are zero, we hide those bar groups for given chart types */
      if (!areAllValuesZero || !removeEmptyBarGroups) {
        result.push({
          xAxis,
          ...incidentCategoryValueMap,
        });
      }

      return result;
    },
    []
  );

  // for grade, it is will sorted by display sequence (done from backend)
  if (_.includes([grade], type)) return formattedGraphData;

  /*
   * For behaviour location, all the zero valued items are sorted to end
   * rest are sorted by label
   */
  if (type === behaviourLocation) {
    // Partition data based on whether the cumulative sum of values is 0
    const [nonZeroSum, zeroSum] = _.partition(
      formattedGraphData,
      item => _.sum(_.values(_.omit(item, "xAxis"))) > 0
    );

    // Sort both groups by xAxis
    const sortedNonZeroSum = _.orderBy(
      nonZeroSum,
      data => _.toLower(data.xAxis),
      "asc"
    );

    const sortedZeroSum = _.orderBy(
      zeroSum,
      data => _.toLower(data.xAxis),
      "asc"
    );

    // Merge sorted non-zero and zero-sum groups
    return [...sortedNonZeroSum, ...sortedZeroSum];
  }

  return _.orderBy(formattedGraphData, data => _.toLower(data.xAxis), "asc");
};

export const getFormattedDataForBehaviourBarChart = ({
  type,
  module,
  variant,
  graphData,
}) => {
  if (variant === "stacked")
    return getFormattedDataForStackedBehaviourBarChart({
      type,
      module,
      graphData,
    });

  return getFormattedDataForSimpleBehaviourBarChart({
    type,
    module,
    graphData,
  });
};

const getBarOptionsForSimpleBarChart = ({ type, formattedGraphData }) => {
  const { behaviourSentiment } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const colorsByEntityId = getAnalyticsEntityColors({
    entityType: type,
  });

  const fill = _.map(formattedGraphData, data => {
    return type === behaviourSentiment
      ? BEHAVIOUR_SENTIMENT_COLOR_MAP[data.xAxis]
      : colorsByEntityId[_.get(data, "id")];
  });

  return [
    {
      dataKey: "yAxis",
      tooltipLabelKey: "xAxis",
      legendLabelKey: "xAxis",
      fill,
    },
  ];
};

const getBarOptionsForStackedBarChart = ({ type, graphData }) => {
  const {
    grade,
    behaviourCategory,
    behaviourLocation,
  } = ANALYTICS_ENTITY_TYPE_ENUMS;
  const entityType = _.includes([grade, behaviourLocation], type)
    ? behaviourCategory
    : type;

  const colorsByCategoryId = getAnalyticsEntityColors({
    entityType,
  });

  const entityIds = getUniqueEntityIds({
    entityType,
    graphData,
  });

  /**
   * If there are no entity ids, then we need to show the bar chart with no data found tooltip
   * This is done to avoid the issue of no bars being shown in the chart
   */
  if (_.isEmpty(entityIds)) return [{}];

  const entityIdNameMap = getEntityIdNameMap({
    entityType,
  });

  return _.reduce(
    entityIds,
    (result, entityId) => {
      const entityLabel = entityIdNameMap[entityId];

      if (!entityLabel) return result;

      result.push({
        dataKey: entityLabel,
        fill: colorsByCategoryId[entityId],
        legendLabel: entityLabel,
        tooltipLabel: entityLabel,
        stackId: "1",
      });

      return result;
    },
    []
  );
};

export const getBarOptionsForBehaviourBarChart = ({
  t,
  type,
  variant,
  graphData,
  formattedGraphData,
}) => {
  if (variant === "stacked")
    return getBarOptionsForStackedBarChart({ type, graphData });

  const { grade, behaviourLocation } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const incidentNomenclatureParams = getNomenclatureLocaleParams({
    type: BEHAVIOUR_NOMENCLATURE_CONSTANTS.INCIDENT,
  });

  switch (type) {
    case grade:
      return [
        {
          dataKey: "yAxis",
          fill: "var(--violet-600)",
          tooltipLabelKey: "xAxis",
          legendLabel: t(
            "toddleAnalytics:total_incidents_with_nomenclature",
            incidentNomenclatureParams
          ),
        },
      ];
    case behaviourLocation:
      return [
        {
          dataKey: "yAxis",
          fill: "var(--purple-600)",
          tooltipLabelKey: "xAxis",
          legendLabel: t(
            "toddleAnalytics:total_incidents_with_nomenclature",
            incidentNomenclatureParams
          ),
        },
      ];
    default: {
      return getBarOptionsForSimpleBarChart({
        type,
        formattedGraphData,
      });
    }
  }
};

export const getBehaviourChartQueryVariablesForAGivenType = params => {
  const {
    type,
    orgId,
    curriculumProgramIds,
    rangeStart = "",
    rangeEnd = "",
    behaviourCategoryIds,
    gradeIds,
    studentIds,
  } = params || {};

  const commonParams = {
    orgId,
    curriculumProgramIds,
    rangeStart,
    rangeEnd,
    gradeIds,
    studentIds,
    behaviourCategoryIds,
  };

  const {
    behaviourCategory,
    behaviourSeverity,
    grade,
    behaviourLocation,
    behaviourAction,
    behaviourSentiment,
  } = ANALYTICS_ENTITY_TYPE_ENUMS;

  switch (type) {
    case behaviourCategory:
      return getBehaviourChartQueryVariables({
        ...commonParams,
        xAxisKey: "BEHAVIOUR_CATEGORY",
        yAxisKey: "INCIDENT_COUNT",
      });
    case behaviourSeverity:
      return getBehaviourChartQueryVariables({
        ...commonParams,
        xAxisKey: "BEHAVIOUR_SEVERITY",
        yAxisKey: "INCIDENT_COUNT",
      });
    case grade:
      return getBehaviourChartQueryVariables({
        ...commonParams,
        xAxisKey: "GRADE",
        yAxisKey: "INCIDENT_COUNT",
      });
    case behaviourLocation:
      return getBehaviourChartQueryVariables({
        ...commonParams,
        xAxisKey: "BEHAVIOUR_LOCATION",
        yAxisKey: "INCIDENT_COUNT",
      });
    case behaviourAction:
      return getBehaviourChartQueryVariables({
        ...commonParams,
        xAxisKey: "BEHAVIOUR_ACTION",
        yAxisKey: "INCIDENT_COUNT",
      });
    case behaviourSentiment:
      return getBehaviourChartQueryVariables({
        ...commonParams,
        xAxisKey: "BEHAVIOUR_SENTIMENT",
        yAxisKey: "INCIDENT_COUNT",
      });
    default:
      return {};
  }
};

export const getFormattedDataForBehaviourPieChart = ({
  type,
  graphData,
  filterZeroValues,
}) => {
  const { behaviourAction } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const colorsByCategoryId = getAnalyticsEntityColors({
    entityType: type,
  });
  const metaDataCategoryId = ENTITY_TYPE_TO_METADATA_ID_MAP[type];

  let colorIndex = 0;
  return _.reduce(
    graphData,
    (result, data) => {
      const name = _.get(data, "xAxis", "");
      const value = trimNumber(_.get(data, "yAxis.0.value", 0));

      /** If value is zero, we hide those segments for given chart types */
      if (value === 0 && filterZeroValues) {
        return result;
      }

      const categoryId = _.get(data, `yAxis.0.metaData.${metaDataCategoryId}`);

      colorIndex = colorIndex % _.size(ACTION_OVERVIEW_CHART_COLORS);
      const segmentFill =
        type === behaviourAction
          ? ACTION_OVERVIEW_CHART_COLORS[colorIndex]
          : colorsByCategoryId[categoryId];

      result.push({
        name,
        value,
        segmentFill,
      });

      colorIndex++;
      return result;
    },
    []
  );
};

const _getStatusIcon = ({ status }) => {
  switch (status) {
    case INCIDENT_STATUS["DRAFT"]: {
      return <PlaceholderdotsOutlined variant="warning" />;
    }

    case INCIDENT_STATUS["PUBLISHED"]: {
      return <ClipboardArrowOutlined variant="default" />;
    }

    case INCIDENT_STATUS["RESOLVED"]: {
      return <AssessmentApplicableOutlined variant="success" />;
    }
    default:
      /*
      Backend is sending wrong statuses for now. So in default case if we send null
      React will throw error because the icon which is returned will be a child of tooltip
      and children cant be null for Tooltip
      */
      return <>-</>;
  }
};

export const getStatusIcon = _.memoize(_getStatusIcon, ({ status }) =>
  JSON.stringify({ status })
);

export const removeStudentInvolvedColumn = ({ tableColumns }) =>
  _.filter(tableColumns, column => !_.isEqual(column.id, "studentInvolved"));

const _applyNomenclatureOnTableColumns = ({ tableColumns }) => {
  return _.map(tableColumns, column => {
    const nomenclatureProps = _.get(column, "nomenclatureProps");
    if (_.isEmpty(nomenclatureProps)) return column;

    const label = _.get(column, "label");
    const type = _.get(nomenclatureProps, "type");
    const convertToLowerCase = _.get(
      nomenclatureProps,
      "convertToLowerCase",
      true
    );
    const nomenclatureParams = getNomenclatureLocaleParams({
      type,
      convertToLowerCase,
    });

    return {
      ...column,
      label: {
        key: label,
        params: nomenclatureParams,
      },
    };
  });
};

export const applyNomenclatureOnTableColumns = _.memoize(
  _applyNomenclatureOnTableColumns,
  ({ tableColumns }) =>
    JSON.stringify({
      tableColumns: _.map(tableColumns, column => ({
        nomenclature: _.get(column, "nomenclatureProps"),
        label: _.get(column, "label"),
        id: _.get(column, "id"),
      })),
    })
);

export const getUniqueEntityIds = ({ entityType, graphData }) => {
  const metaDataCategoryId = ENTITY_TYPE_TO_METADATA_ID_MAP[entityType];

  const result = _.uniq(
    _.compact(
      _.flatten(
        _.map(graphData, data => {
          const yAxis = _.get(data, "yAxis", []);
          return _.map(yAxis, yAxisData => {
            const entityId = _.get(yAxisData, `metaData.${metaDataCategoryId}`);
            return entityId === "null" ? undefined : entityId;
          });
        })
      )
    )
  );

  return result;
};

export const getActionsWithReferralLabel = ({ t, actions }) => {
  const [updatedActions, referredActions] = _.partition(
    actions,
    ({ action_name }) => !_.isEqual(action_name, "Referral")
  );

  const formattedReferralActions = _.map(
    referredActions,
    ({ referrer_name, ...action }) => {
      return {
        ...action,
        action_name: t("toddleAnalytics:refer_to_label", {
          label: referrer_name,
        }),
      };
    }
  );

  updatedActions.push(...formattedReferralActions);

  return updatedActions;
};

export const shouldRemoveEmptyBarGroups = ({ type, module }) => {
  const {
    behaviourLocation,
    behaviourSeverity,
    behaviourCategory,
  } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const { STUDENT_BEHAVIOUR, OVERVIEW_BEHAVIOUR } = BEHAVIOUR_MODULES;

  return (
    _.includes([behaviourSeverity], type) ||
    (type === behaviourCategory && module === STUDENT_BEHAVIOUR) ||
    (type === behaviourLocation && module !== OVERVIEW_BEHAVIOUR)
  );
};

const _getFormattedUserObjInSharedWith = obj => {
  const firstName = _.get(obj, "first_name");
  const lastName = _.get(obj, "last_name");
  const id = _.get(obj, "id");
  const profileUrl = _.get(obj, "profile_url");
  // default is undefined because family/student doesnt have roles
  const role = _.get(obj, "role", undefined);

  const name = `${firstName} ${lastName}`;

  return {
    id,
    name,
    profileUrl,
    role,
  };
};

const countIndividualAdmins = ({ data }) => {
  const filteredStaff = _.filter(data, obj =>
    _.isEqual(obj.role, STAFF_ROLES["ADMIN"])
  );
  return _.size(filteredStaff);
};

const getFormattedUserObjInSharedWith = _.memoize(
  _getFormattedUserObjInSharedWith
);

const _getFormattedSharedWithData = ({ data, t }) => {
  const formattedData = [];

  const homeroom = _.get(data, "homeroom", []);
  const family = _.get(data, "family", []);
  const student = _.get(data, "student", []);
  const staff = _.get(data, "staff", {});

  if (!_.isEmpty(homeroom)) {
    formattedData.push({
      type: "homeroom",
      label: t("common:homeroom"),
      listHeader: t("common:homeroom"),
      sharedWith: _.map(homeroom, obj => getFormattedUserObjInSharedWith(obj)),
    });
  }

  const allStaff = _.get(staff, "allStaff");
  const allAdmin = _.get(staff, "allAdmin");
  const adminCount = _.toNumber(_.get(staff, "adminCount", 0));
  const staffData = _.get(staff, "staff", []);
  const staffDataSize = _.size(staffData);
  const showStaffLabel = staffDataSize > 0 || allAdmin || allStaff;
  const individualAdmins = allAdmin
    ? countIndividualAdmins({ data: staffData })
    : 0;
  const totalStaffCount = adminCount + staffDataSize - individualAdmins;

  if (showStaffLabel) {
    formattedData.push({
      type: "staff",
      fetchAdmins: allAdmin,
      fetchStaff: allStaff,
      label: allStaff
        ? t("common:all_staff")
        : t("toddleAnalytics:staff_with_count", {
            count: totalStaffCount,
          }),
      sharedWith: _.map(staffData, obj => getFormattedUserObjInSharedWith(obj)),
    });
  }

  if (!_.isEmpty(family)) {
    formattedData.push({
      type: "family",
      listHeader: t("common:family_member_plural"),
      label: t("common:family"),
      sharedWith: _.map(family, obj => getFormattedUserObjInSharedWith(obj)),
    });
  }

  if (!_.isEmpty(student)) {
    formattedData.push({
      type: "student",
      label: t("common:student"),
      listHeader: t("common:student_plural"),
      sharedWith: _.map(student, obj => getFormattedUserObjInSharedWith(obj)),
    });
  }

  return formattedData;
};

export const getFormattedSharedWithData = _.memoize(
  _getFormattedSharedWithData,
  ({ data: { homeroom, family, student, staff } = {} }) =>
    JSON.stringify({ homeroom, family, student, staff })
);

const getFormattedStaffData = ({ data }) =>
  _.map(data, obj => {
    const id = _.get(obj, "entityId");
    const name = _.get(obj, "name");
    const profileUrl = _.get(obj, "metaData.profile_url");

    return {
      id,
      name,
      profileUrl,
    };
  });

const _getGroupObject = ({ groups, groupType }) =>
  _.find(groups, group => _.isEqual(group.type, groupType));

export const getGroupObject = _.memoize(
  _getGroupObject,
  ({ groups, groupType }) =>
    JSON.stringify({ groups: _.map(groups, "type"), groupType })
);

const _getFormattedSharedWithOverlayData = ({
  sharedWithData = {},
  adminData = {},
  teacherData = {},
  t,
  type,
  showAllGroups = false,
  allGroupsData,
}) => {
  if (_.includes(["family", "student", "homeroom"], type))
    return [{ ...sharedWithData, showListHeader: false }];

  let staffData = sharedWithData;

  if (showAllGroups) {
    staffData = getGroupObject({ groups: allGroupsData, groupType: "staff" });
  }

  const adminsInSharedWithData = _.filter(staffData.sharedWith, data =>
    _.isEqual(data.role, STAFF_ROLES["ADMIN"])
  );
  const teachersInSharedWithData = _.filter(staffData.sharedWith, data =>
    _.isEqual(data.role, STAFF_ROLES["TEACHER"])
  );
  const archivedInSharedWithData = _.filter(staffData.sharedWith, data =>
    _.isEqual(data.role, STAFF_ROLES["ARCHIVED"])
  );

  const formattedAdminDataFromQuery = getFormattedStaffData({
    data: adminData.data,
  });
  const formattedTeacherDataFromQuery = getFormattedStaffData({
    data: teacherData.data,
  });

  const { fetchAdmins = false, fetchStaff = false } = staffData;

  const finalAdminsData = {
    listHeader: t("common:administrator_plural"),
    showListHeader: true,
    sharedWith:
      fetchAdmins || fetchStaff
        ? formattedAdminDataFromQuery
        : adminsInSharedWithData,
    ...(fetchAdmins || fetchStaff ? adminData : {}),
  };

  const finalTeachersData = {
    listHeader: t("common:teacher_plural"),
    showListHeader: true,
    sharedWith: fetchStaff
      ? formattedTeacherDataFromQuery
      : teachersInSharedWithData,
    ...(fetchStaff ? teacherData : {}),
  };

  const finalArchivedData = {
    listHeader: t("toddleAnalytics:archived_staff"),
    showListHeader: true,
    sharedWith: archivedInSharedWithData,
  };

  if (showAllGroups) {
    const finalHomeroomData = getGroupObject({
      groups: allGroupsData,
      groupType: "homeroom",
    });
    const finalFamilyData = getGroupObject({
      groups: allGroupsData,
      groupType: "family",
    });
    const finalStudentData = getGroupObject({
      groups: allGroupsData,
      groupType: "student",
    });

    const allGroupsDataWithOrder = [
      finalAdminsData,
      finalHomeroomData,
      finalTeachersData,
      finalFamilyData,
      finalStudentData,
    ];

    const filteredAllGroupsDataWithOrder = _.compact(allGroupsDataWithOrder);

    return _.map(filteredAllGroupsDataWithOrder, group => ({
      ...group,
      showListHeader: true,
    }));
  }

  return [finalAdminsData, finalTeachersData, finalArchivedData];
};

export const getFormattedSharedWithOverlayData = _.memoize(
  _getFormattedSharedWithOverlayData,
  ({
    sharedWithData = {},
    adminData = {},
    teacherData = {},
    type,
    showAllGroups = false,
    allGroupsData,
  }) =>
    JSON.stringify({
      sharedWithData,
      adminData,
      teacherData,
      type,
      showAllGroups,
      allGroupsData,
    })
);

const _getStaffVariables = ({ organizationId, academicYearId, role }) => {
  return {
    id: organizationId,
    analyticsEntityFilterInput: {
      entityType: ANALYTICS_ENTITY_TYPE_ENUMS["staff"],
      role,
      academicYearId,
      paginationFilters: { first: 25 },
    },
  };
};

export const getStaffVariables = _.memoize(
  _getStaffVariables,
  ({ organizationId, academicYearId, role }) =>
    JSON.stringify({ organizationId, academicYearId, role })
);

export const getFormattedStaffDataFromQuery = ({
  data,
  networkStatus,
  loadMore,
}) => {
  const allStaffList = _.get(data, "edges", []);
  const totalCount = _.get(data, "totalCount", 0);

  const { hasNextPage } = _.get(data, "pageInfo", {});

  return {
    data: allStaffList,
    hasNextPage: hasNextPage,
    isLoadingMore: _.isEqual(networkStatus, 3),
    totalCount,
    loadMore,
  };
};
